import React, { useEffect, useState } from 'react';
import { FxInput, http, AlertService, formValidator, general, FxSelect, FxTextarea } from '../../fx-core';
import './SpecialCommunityForm.scss';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import dummyImage from "../../../assets/images/dummy.png";
import PhotoCapture from '../PhotoCapture/PhotoCapture';
import { Button, Dialog, Checkbox } from '@material-ui/core';


const SpecialCommunityForm = (props) => {
  const defaultItem = {
    profileImagePath: "",
    profileImageUrl: "",
    name: "",
    code: "",
    address: "",
    lat: "",
    lng: "",
    landmark: "",
    entryFee: "",
    id: props.communityId ? props.communityId : 0,
    communityTypeId: 2,
    visibleCommunityId: 0,
    isStallCreation: false,
    isFeatured: false,
    displayOrder: "0",
    startDate: "",
    startTime: "00:00",
    endDate: "",
    endTime: "23:59"
  };


  const [item, setItem] = useState(defaultItem);
  const [errors, setErrors] = useState({});
  const [photoCaptureOpen, setPhotoCaptureOpen] = useState(false);
  const [uploadConfig, setUploadConfig] = useState({});
  const [error, setError] = useState(false);
  const [visibleCommunities, setVisibleCommunities] = useState([]);
  const [codeValidation, setCodeValidation] = useState(false);
  const [rangeError, setRangeError] = useState(null);


  // Parse date safely
  const toDate = (val) => {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };


  // Parse time safely
  const toHM = (val) => {
    if (!val) return null;
    const s = val.trim().toUpperCase();


    // Handle 12h format "2:30 PM"
    const match12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
    if (match12) {
      let h = parseInt(match12[1], 10);
      const m = parseInt(match12[2], 10);
      if (match12[3] === "PM" && h < 12) h += 12;
      if (match12[3] === "AM" && h === 12) h = 0;
      return { h, m };
    }


    // Handle 24h format "14:30"
    const match24 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (match24) {
      const h = parseInt(match24[1], 10);
      const m = parseInt(match24[2], 10);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) return { h, m };
    }


    return null;
  };


  // Combine date+time into local datetime string
  const combineToLocalDateTime = (dateVal, timeVal) => {
    const d = toDate(dateVal);
    if (!d) return null;


    let hm = toHM(timeVal);
    if (!hm) return null;


    d.setHours(hm.h, hm.m, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  };


  const initMethods = () => {
    initLookup();
    if (props.communityId > 0) {
      getCommunityById(props.communityId);
    }
  };


  const initLookup = () => {
    let inputData = {
      lookups: { CommunityType: { default: true } },
      filters: {}
    };
    http.post({ url: 'options/lookup', data: inputData }).then(() => {
      initLookupCallBack();
    });
  };


  const initLookupCallBack = () => {
    if (props.isRegional) {
      getRegionVisibleCommunities();
    } else {
      getZoneVisibleCommunities();
    }
  };


  const getRegionVisibleCommunities = () => {
    http.get({ url: `community/visiblecommunity/region` }).then(res => {
      setVisibleCommunities(res.data);
    });
  };


  const getZoneVisibleCommunities = () => {
    http.get({ url: `community/visiblecommunity/zone` }).then(res => {
      setVisibleCommunities(res.data);
    });
  };


  const handleInputChange = (input) => {
    handleItemChange({ [input.name]: input.value });
    input.validations = getInputValidations(input.name);
    const validationResult = formValidator.validate(input);
    updateErrors({ [input.name]: validationResult });
    if (['startDate', 'startTime', 'endDate', 'endTime'].includes(input.name)) setRangeError(null);
  };


  const handleItemChange = (data) => {
    setItem(prevState => ({ ...prevState, ...data }));
  };


  const handleCheckbox = (event) => {
    handleItemChange({ [event.target.name]: event.target.checked });
  };


  const validationMap_Item = {
    code: ["required"],
    name: ["required"],
    startDate: ["required"],
    startTime: ["required"],
    endDate: ["required"],
    endTime: ["required"]
  };


  const getValidations = () => validationMap_Item;
  const getInputValidations = (name) => getValidations()[name];


  const bulkValidate = () => {
    let items = [];
    let validationMap = getValidations();
    for (var key in validationMap) {
      items.push({ name: key, value: item[key], validations: validationMap[key] });
    }
    let validationResult = formValidator.bulkValidate(items);
    updateErrors(validationResult.errors);
    return validationResult.isValid;
  };


  const updateErrors = (validationData) => {
    setErrors(prevState => ({ ...prevState, ...validationData }));
  };


  const hasError = (field, validationMethod) => {
    return errors && errors[field] && errors[field][validationMethod];
  };


  const getCommunityById = (commtyId) => {
    http.get({ url: `community/${commtyId}` }).then(res => {
      const result = res.data;
      result.profileImageUrl = general.getImageUrl(result.profileImagePath);

      // Parse startDateTime from response format "10/11/2025 3:11:00 AM"
      if (result.startDateTime) {
        const startDate = new Date(result.startDateTime);
        if (!isNaN(startDate.getTime())) {
          result.startDate = startDate.toISOString().split("T")[0]; // yyyy-mm-dd
          result.startTime = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
        }
      }

      // Parse endDateTime from response format "10/11/2025 11:11:00 PM"
      if (result.endDateTime) {
        const endDate = new Date(result.endDateTime);
        if (!isNaN(endDate.getTime())) {
          result.endDate = endDate.toISOString().split("T")[0];
          result.endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
        }
      }

      // Ensure entryFee is properly set (note the lowercase)
      result.entryFee = result.entryFee !== null && result.entryFee !== undefined ? result.entryFee.toString() : "";

      setItem(result);
    });
  };

  // ✅ NEW: Function to open photo capture with proper config
  const openPhotoCapture = () => {
    let photoCaptureConfig = {
      id: item.id,
      cameraConfig: { url: `community/savecameraimage` },
      fileUploadConfig: { url: `community/savefileimage` }
    };
    setUploadConfig(photoCaptureConfig);
    setPhotoCaptureOpen(true);
  };

  // ✅ NEW: Function to handle upload callback
  const onUploadCallBack = (res) => {
    setPhotoCaptureOpen(false);
    if (item.id > 0) {
      getCommunityById(item.id);  // Refresh the entire community data
    } else {
      let imagePath = res.filePath;
      let imageUrl = general.getImageUrl(imagePath);
      handleItemChange({ profileImagePath: imagePath, profileImageUrl: imageUrl });
    }
  };

  // ✅ NEW: Function to handle photo capture close
  const handlePhotoCaptureClose = () => {
    setPhotoCaptureOpen(false);
  };


  const saveCommunity = () => {
    setCodeValidation(false);
    setError(false);
    setRangeError(null);


    const isValid = bulkValidate();
    if (!isValid) return;


    if (/\s/g.test(item.code)) {
      setCodeValidation(true);
      return;
    }


    const startLocal = combineToLocalDateTime(item.startDate, item.startTime);
    const endLocal = combineToLocalDateTime(item.endDate, item.endTime);


    if (!startLocal || !endLocal) {
      setRangeError("Please provide a valid start and end date/time.");
      return;
    }


    if (new Date(endLocal).getTime() < new Date(startLocal).getTime()) {
      setRangeError("End date/time must be after start date/time.");
      return;
    }


    let payload = {
      ...item,
      startDateTime: startLocal,
      endDateTime: endLocal
    };

    // Handle entryFee conversion (note the lowercase)
    if (payload.entryFee !== "") {
      payload.entryFee = parseFloat(payload.entryFee);
    }


    delete payload.startDate;
    delete payload.startTime;
    delete payload.endDate;
    delete payload.endTime;


    http.post({ url: `community/special`, data: payload }).then(res => {
      AlertService.showSuccessMsg();
      props.handleCommunityFormClose();
    });
  };


  useEffect(() => {
    initMethods();
  }, []);


  return (
    <div className='communityform-ui'>
      <div className='row title-font py-1 px-2 drawer-header'>
        <span className="col-11">Special Community Form</span>
        <span className="col-1 text-center">
          <i className="fas fa-times-circle font-16 pointer" onClick={props.handleCommunityFormClose}></i>
        </span>
      </div>


      <div className='p-3 pt-5'>
        <div className='row p-3'>
          {/* Image */}
          <div className="col-2 text-center">
            <div>
              {!item.profileImageUrl && <img src={dummyImage} alt="dummy" width={100} height={100} />}
              {item.profileImageUrl && <img src={item.profileImageUrl} alt="prod" width={100} height={100} />}
              {/* ✅ FIXED: Use openPhotoCapture function */}
              <div className="text-center camera-align" onClick={openPhotoCapture}>
                <CameraAltIcon className='pointer' />
              </div>

            </div>
            
          </div>

          <div className=' col-12 font-12'> Special Community
          </div>


          {/* Code */}
          <div className='col-12 p-3'>
            <FxInput name="code" variant="outlined" fullWidth size="small" label="Code" disabled={item.id > 0}
              value={item.code} onValueChange={handleInputChange} />
            {hasError("code", "required") && <div className="error">Code is required</div>}
            {codeValidation && <div className="error">Code should not contain space</div>}
          </div>


          {/* Name */}
          <div className='col-12 p-3'>
            <FxInput name="name" variant="outlined" fullWidth size="small" label="Name"
              value={item.name} onValueChange={handleInputChange} />
            {hasError("name", "required") && <div className="error">Name is required</div>}
          </div>


          {/* Address */}
          <div className='col-12 p-3'>
            <FxTextarea name="address" variant="outlined" fullWidth size="small" label="Address"
              value={item.address} onValueChange={handleInputChange} maxRows={4} />
          </div>


          {/* Display Order */}
          <div className='col-12 p-3'>
            <FxInput name="displayOrder" variant="outlined" fullWidth size="small" label="Display Order"
              value={item.displayOrder} onValueChange={handleInputChange} type="number" />
          </div>


          {/* Entry Fee */}
          <div className='col-12 p-3'>
            <FxInput name="entryFee" variant="outlined" fullWidth size="small" label="Entry fee per day"
              type="number" step="0.01" value={item.entryFee} onValueChange={handleInputChange} />
          </div>


          {/* Start Date & Time */}
          <div className='row col-12 p-3'>
            <div className='col-6'>
              <input type="date" name="startDate" value={item.startDate || ""} onChange={(e) => handleInputChange({ name: "startDate", value: e.target.value })} className="form-control" />
              {hasError("startDate", "required") && <div className="error">Start Date is required</div>}
            </div>
            <div className='col-6'>
              <input type="time" name="startTime" value={item.startTime || "00:00"} onChange={(e) => handleInputChange({ name: "startTime", value: e.target.value })} className="form-control" disabled />
              {hasError("startTime", "required") && <div className="error">Start Time is required</div>}
            </div>
          </div>


          {/* End Date & Time */}
          <div className='row col-12 p-3'>
            <div className='col-6'>
              <input type="date" name="endDate" value={item.endDate || ""} onChange={(e) => handleInputChange({ name: "endDate", value: e.target.value })} className="form-control" />
              {hasError("endDate", "required") && <div className="error">End Date is required</div>}
            </div>
            <div className='col-6'>
              <input type="time" name="endTime" value={item.endTime || "23:59"} onChange={(e) => handleInputChange({ name: "endTime", value: e.target.value })} className="form-control" disabled />
              {hasError("endTime", "required") && <div className="error">End Time is required</div>}
              {rangeError && <div className="error">{rangeError}</div>}
            </div>
          </div>


          {/* Visible Community */}
          {item.communityTypeId === 2 && (
            <div className='col-12 p-3'>
              <FxSelect name="visibleCommunityId" variant="outlined" label="Visible Community"
                options={visibleCommunities}
                selectedValue={item.visibleCommunityId}
                valueField="id"
                displayField="name"
                size="small"
                fullWidth
                onValueChange={handleInputChange} />
            </div>
          )}


          {/* Checkboxes */}
          {!props.isRegional && (
            <>
              <div className='col-12 p-2'>
                <Checkbox name="isStallCreation" color="primary"
                  checked={item.isStallCreation} onChange={handleCheckbox} />
                Stall Creation
              </div>
              <div className='col-12 p-2'>
                <Checkbox name="isFeatured" color="primary"
                  checked={item.isFeatured} onChange={handleCheckbox} />
                Is Featured
              </div>
            </>
          )}
        </div>
      </div>


      {/* Footer */}
      <div className='px-4 py-2 drawer-footer'>
        <div className='row px-4'>
          <Button color="primary" variant="contained" fullWidth className='btn-style' onClick={saveCommunity}><b>Save</b></Button>
        </div>
      </div>


      {/* ✅ FIXED: Photo Capture Dialog with proper callbacks */}
      <Dialog open={photoCaptureOpen} onClose={handlePhotoCaptureClose}>
        <PhotoCapture
          uploadConfig={uploadConfig}
          onUploadCallBack={onUploadCallBack}
          handlePhotoCaptureClose={handlePhotoCaptureClose}
        />
      </Dialog>
    </div>
  );
};


export default SpecialCommunityForm;
